import { defaultImg } from "@/config/constConf";

/**
 * 创建一张新的图片，删除两侧空白，并且返回图片和图片宽高
 */
export function removeBlank(
    imgUrl: string,
): Promise<{ dataUrl: string; width: number; height: number; status: number }> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.onload = () => {
            const width = image.width;
            const height = image.height;
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx: CanvasRenderingContext2D = <CanvasRenderingContext2D>(
                canvas.getContext("2d")
            );
            ctx.drawImage(image, 0, 0, width, height);
            const originData = ctx.getImageData(0, 0, width, height).data;
            // 实际上这里的imgData是一个二维数组。外层数组存放像素信息。内层数组存放像素点的rgba
            const imgData: any = [];
            for (let i = 0, len = originData.length; i < len; i++) {
                const idx = parseInt(i / 4 + "", 10);
                if (!imgData[idx]) {
                    imgData[idx] = [];
                }
                imgData[idx].push(originData[i]);
            }
            // 左右上下边界
            let left: number | undefined,
                right: number | undefined,
                top: number | undefined,
                bottom: number | undefined;
            for (let i = 0; i < width; i++) {
                for (let j = 0; j < height; j++) {
                    const [r, g, b] = imgData[j * width + i];
                    if (r !== 0 || g !== 0 || b !== 0) {
                        left = i;
                        break;
                    }
                }
                if (left !== undefined) {
                    break;
                }
            }
            for (let i = width - 1; i >= 0; i--) {
                for (let j = 0; j < height; j++) {
                    const [r, g, b] = imgData[j * width + i];
                    if (r !== 0 || g !== 0 || b !== 0) {
                        right = i;
                        break;
                    }
                }
                if (right !== undefined) {
                    break;
                }
            }
            for (let j = 0; j < height; j++) {
                for (let i = 0; i < width; i++) {
                    // 判断上边界
                    const [r, g, b] = imgData[j * width + i];
                    if (r !== 0 || g !== 0 || b !== 0) {
                        top = j;
                        break;
                    }
                }
                if (top !== undefined) {
                    break;
                }
            }
            for (let j = height - 1; j >= 0; j--) {
                for (let i = 0; i < width; i++) {
                    // 判断下边界
                    const [r, g, b] = imgData[j * width + i];
                    if (r !== 0 || g !== 0 || b !== 0) {
                        bottom = j;
                        break;
                    }
                }
                if (bottom !== undefined) {
                    break;
                }
            }
            canvas.width = right! - left! + 1;
            canvas.height = bottom! - top! + 1;
            ctx.drawImage(image, -left!, -top!, width, height);
            resolve({
                dataUrl: canvas.toDataURL("image/png"),
                width: canvas.width,
                height: canvas.height,
                status: 200,
            });
        };
        image.onerror = () => {
            image.src = defaultImg; // 这里不能抛出去了，会一直去请求图片
            resolve({
                status: 404,
                dataUrl: defaultImg,
                width: 0,
                height: 0,
            });
        };
        //试戴经过处理后的图片后面跟参数会报错
        image.src =
            imgUrl.indexOf("data") > -1
                ? imgUrl
                : `${imgUrl}?t=${Date.now()}${Math.random()
                      .toString(16)
                      .substring(2)}`;
    });
}

/**
 * 拿到图片的宽高
 * @param imgUrl 图片的url
 * @returns {Promise<{width:number,height:number}>}
 */
export function getImgWidthHeight(
    imgUrl: string,
): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.onload = () => {
            resolve({ width: image.width, height: image.height });
        };
        image.onerror = () => {
            reject("img load error");
        };
        image.src = imgUrl;
    });
}

/**
 * 把用户上传的图片进行压缩，返回压缩后的blob和dataurl
 * @param file 用户上传的图片
 * @param type 压缩后的文件类型
 * @param quality 质量
 */
export function compressImg(
    file: File,
    type = "jpeg",
    quality: number | undefined,
): Promise<[Blob, string]> {
    return new Promise((resolve) => {
        const reader = new FileReader(); // 读取文件资源
        reader.onload = (e: any) => {
            const img = new Image();
            img.crossOrigin = "anonymous";

            img.onload = () => {
                const { width: originWidth, height: originHeight } = img;
                // 最大尺寸限制
                const maxWidth = 750,
                    maxHeight = 860;
                // 需要压缩的目标尺寸
                let targetWidth = originWidth,
                    targetHeight = originHeight;
                // 等比例计算超过最大限制时缩放后的图片尺寸
                if (originWidth > maxWidth || originHeight > maxHeight) {
                    if (originWidth / originHeight > 1) {
                        // 图片太宽
                        targetWidth = maxWidth;
                        targetHeight = Math.round(
                            maxWidth * (originHeight / originWidth),
                        );
                    } else {
                        // 图片太高
                        targetHeight = maxHeight;
                        targetWidth = Math.round(
                            maxHeight * (originWidth / originHeight),
                        );
                    }
                }

                const canvas = document.createElement("canvas");
                const context: CanvasRenderingContext2D = <
                    CanvasRenderingContext2D
                >canvas.getContext("2d");

                // 设置宽高度为等同于要压缩图片的尺寸
                canvas.width = targetWidth;
                canvas.height = targetHeight;
                context.clearRect(0, 0, targetWidth, targetHeight);
                context.fillRect(0, 0, targetWidth, targetHeight);
                //将img绘制到画布上
                context.drawImage(img, 0, 0, targetWidth, targetHeight);
                const dataURL =
                    quality === undefined
                        ? canvas.toDataURL(`image/${type}`)
                        : canvas.toDataURL(`image/${type}`, quality);
                canvas.toBlob(function (blob: any) {
                    resolve([blob, dataURL]);
                }, `image/${type}`);
            };
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    });
}

export function file2dataUrl(file: File): Promise<string> {
    return new Promise((resolve) => {
        let fileReader = new FileReader();
        fileReader.onload = (ev: any) => {
            resolve(ev.target.result);
        };
        fileReader.readAsDataURL(file);
    });
}

// 根据图片、图片宽高、旋转、装img的盒子的宽高、裁剪区域的位置。获取截取的部分。返回blob和dataUrl
interface ICropImage {
    dataUrl: string;
    imgWidth: number;
    imgHeight: number;
    angle: number;
    boxWidth: number;
    boxHeight: number;
    left: number;
    top: number;
    width: number;
    height: number;
}
// 传入的参数最好不要超过三个，否则就应该做出其他处理，比如搞成对象
export function cropImg({
    dataUrl,
    imgWidth,
    imgHeight,
    angle,
    boxWidth,
    boxHeight,
    left,
    top,
    width,
    height,
}: ICropImage): Promise<any> {
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";

        img.onload = () => {
            const needExchange = ((360 - angle) / 90) % 2 === 1;
            const rotate = ((angle / 90) * Math.PI) / 2;

            const { width: imgWidth, height: imgHeight } = img;
            const tooHigh = imgHeight / imgWidth > boxHeight / boxWidth;
            let finalWidth = tooHigh
                ? (boxHeight * imgWidth) / imgHeight
                : boxWidth;
            let finalHeight = tooHigh
                ? boxHeight
                : (boxWidth * imgHeight) / imgWidth;
            if (needExchange) {
                finalWidth = tooHigh
                    ? (boxWidth * imgWidth) / imgHeight
                    : boxHeight;
                finalHeight = tooHigh
                    ? boxWidth
                    : (boxHeight * imgHeight) / imgWidth;
            }

            const canvas = document.createElement("canvas");
            const ctx: CanvasRenderingContext2D = <CanvasRenderingContext2D>(
                canvas.getContext("2d")
            );
            canvas.width = boxWidth;
            canvas.height = boxHeight;
            ctx.translate(canvas.width / 2, canvas.height / 2);
            ctx.rotate(rotate);
            ctx.drawImage(
                img,
                -finalWidth / 2,
                -finalHeight / 2,
                finalWidth,
                finalHeight,
            );

            const imgData = ctx.getImageData(left, top, width, height);
            const canvas1 = document.createElement("canvas");
            const ctx1: CanvasRenderingContext2D = <CanvasRenderingContext2D>(
                canvas1.getContext("2d")
            );
            canvas1.width = width;
            canvas1.height = height;
            ctx1.putImageData(imgData, 0, 0);
            canvas1.toBlob((blob) => {
                resolve({
                    blob,
                    dataUrl: canvas1.toDataURL("image/jpeg"),
                });
            }, "image/jpeg");
        };
        img.src = dataUrl;
    });
}
